
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { X, CheckCircle, AlertCircle, Info, HelpCircle, Copy } from 'lucide-react';

interface Toast {
  id: string;
  type: 'success' | 'error' | 'info';
  message: string;
}

interface ModalContextType {
  toast: {
    success: (msg: string) => void;
    error: (msg: string) => void;
    info: (msg: string) => void;
  };
  confirm: (options: { title: string; message: string; confirmText?: string; cancelText?: string; danger?: boolean }) => Promise<boolean>;
  prompt: (options: { title: string; message: string; placeholder?: string; confirmText?: string; cancelText?: string; expectedValue?: string }) => Promise<string | null>;
  alert: (options: { title: string; message: string; buttonText?: string }) => Promise<void>;
  choice: (options: { title: string; message: string; option1: { text: string; danger?: boolean }, option2: { text: string; danger?: boolean }, cancelText?: string }) => Promise<'option1' | 'option2' | null>;
}

const ModalContext = createContext<ModalContextType | null>(null);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) throw new Error("useModal must be used within ModalProvider");
  return context;
};

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Toast State
  const [toasts, setToasts] = useState<Toast[]>([]);
  
  // Modal State
  const [modalConfig, setModalConfig] = useState<any>(null);
  const awaiter = useRef<(value: any) => void>(null);

  // Toast Helpers
  const addToast = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  const toast = {
    success: (msg: string) => addToast('success', msg),
    error: (msg: string) => addToast('error', msg),
    info: (msg: string) => addToast('info', msg),
  };

  // Modal Helpers
  const confirm = useCallback(({ title, message, confirmText = 'Confirmer', cancelText = 'Annuler', danger = false }: any) => {
    return new Promise<boolean>((resolve) => {
      setModalConfig({ type: 'confirm', title, message, confirmText, cancelText, danger });
      awaiter.current = resolve;
    });
  }, []);

  const prompt = useCallback(({ title, message, placeholder = '', confirmText = 'Valider', cancelText = 'Annuler', expectedValue }: any) => {
    return new Promise<string | null>((resolve) => {
      setModalConfig({ type: 'prompt', title, message, placeholder, confirmText, cancelText, expectedValue });
      awaiter.current = resolve;
    });
  }, []);

  const alertModal = useCallback(({ title, message, buttonText = 'OK' }: any) => {
    return new Promise<void>((resolve) => {
      setModalConfig({ type: 'alert', title, message, confirmText: buttonText });
      awaiter.current = resolve;
    });
  }, []);

  const choice = useCallback(({ title, message, option1, option2, cancelText = 'Annuler' }: any) => {
    return new Promise<'option1' | 'option2' | null>((resolve) => {
      setModalConfig({ type: 'choice', title, message, option1, option2, cancelText });
      awaiter.current = resolve;
    });
  }, []);

  const handleClose = (value: any) => {
    setModalConfig(null);
    if (awaiter.current) {
      awaiter.current(value);
      awaiter.current = null; // Clear ref
    }
  };

  // Internal component for Modal Display
  const ModalDisplay = () => {
    if (!modalConfig) return null;
    
    const [inputValue, setInputValue] = useState('');
    const [copied, setCopied] = useState(false);

    // Vérifier si la valeur saisie correspond à la valeur attendue (si spécifiée)
    const isValid = modalConfig.expectedValue 
      ? inputValue.trim() === modalConfig.expectedValue.trim()
      : inputValue.trim().length > 0;

    const handleCopy = async () => {
      if (modalConfig.expectedValue) {
        try {
          await navigator.clipboard.writeText(modalConfig.expectedValue);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          console.error('Failed to copy:', err);
        }
      }
    };

    const onSubmit = (choiceValue?: 'option1' | 'option2') => {
        if (modalConfig.type === 'prompt' && isValid) {
          handleClose(inputValue);
        } else if (modalConfig.type === 'confirm') {
          handleClose(true);
        } else if (modalConfig.type === 'alert') {
          handleClose(true);
        } else if (modalConfig.type === 'choice' && choiceValue) {
          handleClose(choiceValue);
        }
    };

    const onCancel = () => {
        if (modalConfig.type === 'prompt') handleClose(null);
        else handleClose(false);
    };

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden scale-100 animate-in zoom-in-95 duration-200 border border-slate-100">
          <div className="p-6">
            <div className="flex items-start mb-4">
               {modalConfig.danger ? <div className="p-2 bg-red-100 rounded-full mr-3 text-red-600 shrink-0"><AlertCircle size={24} /></div> :
                modalConfig.type === 'prompt' ? <div className="p-2 bg-blue-100 rounded-full mr-3 text-blue-600 shrink-0"><HelpCircle size={24} /></div> :
                <div className="p-2 bg-amber-100 rounded-full mr-3 text-amber-600 shrink-0"><Info size={24} /></div>
               }
               <div className="flex-1">
                   <h3 className="text-lg font-bold text-slate-900">{modalConfig.title}</h3>
                   <div className="text-slate-500 text-sm mt-1 whitespace-pre-line">
                       {modalConfig.type === 'prompt' && modalConfig.expectedValue ? (
                           <div>
                               <div className="flex items-center gap-2 flex-wrap">
                                   <span>{modalConfig.message}</span>
                                   <span className="font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded">"{modalConfig.expectedValue}"</span>
                                   <button
                                       onClick={handleCopy}
                                       className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors inline-flex items-center"
                                       title="Copier le nom dans le presse-papier"
                                   >
                                       {copied ? (
                                           <span className="text-xs text-green-600 font-medium">✓ Copié</span>
                                       ) : (
                                           <Copy className="w-3.5 h-3.5" />
                                       )}
                                   </button>
                               </div>
                           </div>
                       ) : (
                           modalConfig.message
                       )}
                   </div>
               </div>
            </div>

            {modalConfig.type === 'prompt' && (
                <div className="mb-4">
                    <input 
                        autoFocus
                        className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none text-slate-900 bg-slate-50 ${
                            modalConfig.expectedValue && inputValue.trim() !== modalConfig.expectedValue.trim() && inputValue.trim().length > 0
                              ? 'border-red-300 bg-red-50'
                              : modalConfig.expectedValue && isValid
                              ? 'border-green-300 bg-green-50'
                              : 'border-slate-300'
                        }`}
                        placeholder={modalConfig.placeholder}
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && isValid && onSubmit()}
                    />
                    {modalConfig.expectedValue && inputValue.trim().length > 0 && !isValid && (
                        <p className="text-xs text-red-600 mt-1">Le nom saisi ne correspond pas</p>
                    )}
                    {modalConfig.expectedValue && isValid && (
                        <p className="text-xs text-green-600 mt-1">✓ Nom correct</p>
                    )}
                </div>
            )}

            {modalConfig.type === 'choice' ? (
                <div className="flex flex-col gap-3 mt-4">
                    <button 
                        onClick={() => onSubmit('option1')}
                        className={`px-4 py-3 text-white rounded-lg font-bold shadow-sm transition-colors ${
                            modalConfig.option1.danger 
                            ? 'bg-red-600 hover:bg-red-700' 
                            : 'bg-slate-900 hover:bg-slate-800'
                        }`}
                    >
                        {modalConfig.option1.text}
                    </button>
                    <button 
                        onClick={() => onSubmit('option2')}
                        className={`px-4 py-3 text-white rounded-lg font-bold shadow-sm transition-colors ${
                            modalConfig.option2.danger 
                            ? 'bg-red-600 hover:bg-red-700' 
                            : 'bg-slate-900 hover:bg-slate-800'
                        }`}
                    >
                        {modalConfig.option2.text}
                    </button>
                    <button onClick={onCancel} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium transition-colors">
                        {modalConfig.cancelText || 'Annuler'}
                    </button>
                </div>
            ) : (
                <div className="flex justify-end space-x-3 mt-4">
                    {modalConfig.type !== 'alert' && (
                        <button onClick={onCancel} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium transition-colors">
                            {modalConfig.cancelText || 'Annuler'}
                        </button>
                    )}
                    <button 
                        onClick={() => onSubmit()}
                        disabled={modalConfig.type === 'prompt' && !isValid}
                        className={`px-4 py-2 text-white rounded-lg font-bold shadow-sm transition-colors ${
                            modalConfig.type === 'prompt' && !isValid
                              ? 'bg-slate-400 cursor-not-allowed'
                              : modalConfig.danger 
                              ? 'bg-red-600 hover:bg-red-700' 
                              : 'bg-slate-900 hover:bg-slate-800'
                        }`}
                    >
                        {modalConfig.confirmText || 'Confirmer'}
                    </button>
                </div>
            )}
          </div>
        </div>
      </div>
    );
  };

    return (
    <ModalContext.Provider value={{ toast, confirm, prompt, alert: alertModal, choice }}>
      {children}
      
      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-[110] flex flex-col space-y-2 pointer-events-none">
        {toasts.map(t => (
            <div key={t.id} className="pointer-events-auto bg-white border-l-4 p-4 rounded-lg shadow-xl flex items-start w-80 animate-in slide-in-from-right duration-300 ring-1 ring-slate-100"
                 style={{ borderColor: t.type === 'success' ? '#10b981' : t.type === 'error' ? '#ef4444' : '#3b82f6' }}>
                <div className="mr-3 mt-0.5">
                    {t.type === 'success' && <CheckCircle className="w-5 h-5 text-emerald-500" />}
                    {t.type === 'error' && <AlertCircle className="w-5 h-5 text-red-500" />}
                    {t.type === 'info' && <Info className="w-5 h-5 text-blue-500" />}
                </div>
                <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{t.message}</p>
                </div>
                <button onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))} className="ml-2 text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4" />
                </button>
            </div>
        ))}
      </div>

      <ModalDisplay />
    </ModalContext.Provider>
  );
};
